import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector, DefaultRootState } from 'react-redux';
import usePagination from '../../hooks/usePagination';
import {
    getAllStaffs,
    getV2AllStaffs,
    getV2AllStaffCounts,
    getShiftTimingsList,
    resetKycDetails,
    getAllBlueDartStaffs,
    getAllBlueDartCounts,
    getBluedartFilterVendors,
    getAllBluedartHubs,
    getAllBluedartServiceCentreCities,
    getAllBluedartServiceCentreZones,
    updateStatus,
} from '../../store/actions/staffActionCreator';
import { preProcessPayload, RIDER_FILTER_CARDS } from './rider.helper';
import { raiseBluedartReactivation } from '../../store/actions/staffActionCreator';
import { Box, Button, Tooltip, Typography } from '@material-ui/core';
import useRiderManagementStyles from './useRiderManagementStyles';
import clsx from 'clsx';
import useCommonStyles from '../../components/UI/CommonStyles';
import {
    getActiveInitRoute,
    getPermissions,
    showMessage,
} from '../../utils/helper';
import {
    getBdScopeType,
    getBdScopeValues,
    isBdRbacScopingEnabled,
    isBlueDartRiderApprover,
    isBdDeactivationEnabled,
} from '../../utils/appHelper';
import VisibilityIcon from '@material-ui/icons/Visibility';
import BlockIcon from '@material-ui/icons/Block';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import ReplayIcon from '@material-ui/icons/Replay';
import { isMpinEnabled } from '../../utils/appHelper';
import { ActionIcon, CellText, CopyableChip } from './utility.component.rider';

interface UseRiderManagementProps {
    externalFiltered?: any;
    history?: any;
    [key: string]: any;
}

const useRiderManagement = (props: UseRiderManagementProps) => {
    const containerRef = useRef<HTMLElement>(null);
    const dispatch = useDispatch();

    const initialPage = 0;
    const initialPageSize = 25;
    const initialActiveFilterCard = {
        name: 'Onboarded',
        type: 'downloadPending',
        subType: '',
        filterStatus: 'downloadPending',
        valueMappingKey: 'downloadPending',
    };
    const initialFilterValues = {};
    const initialSearchText = '';
    const initialPayload = {
        pageNo: initialPage,
        pageSize: initialPageSize,
        searchText: '',
        activeFilterCard: initialActiveFilterCard,
        filters: {
            ...initialFilterValues,
            ...props?.externalFiltered,
        },
        ...props?.externalFiltered,
        hubIds: props?.externalFiltered?.hubIds?.map((hub: any) => hub?.value),
    };
    let disableAutoSearch = true;
    const {
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        activeFilterCard,
        filterValues,
        searchTextValue,
        payload,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
        setFilterValues,
        clearSearch,
        onManualSearch,
        onManualSearchClear,
    } = usePagination(
        initialPage,
        initialPageSize,
        initialActiveFilterCard,
        initialFilterValues,
        initialSearchText,
        initialPayload,
        disableAutoSearch,
    );

    const [loading, setLoading] = useState(false);
    const [countsLoading, setCountsLoading] = useState(false);
    const [selectedRider, setSelectedRider] = useState<any>(null);
    const [selected, setSelected] = useState<any[]>([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [deactivateConfirmRider, setDeactivateConfirmRider] =
        useState<any>(null);

    // Staff row whose MPIN summary/expire modal is open (null = closed).
    const [mpinRider, setMpinRider] = useState<any>(null);
    const [reactivateConfirmRider, setReactivateConfirmRider] =
        useState<any>(null);
    const [openBulkModal, setBulkModalOpen] = useState(false);
    const [bulkUploadType, setBulkUploadType] = useState(null);
    const [containerHeight, setContainerHeight] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [riderCounts, setRiderCounts] = useState({
        totalCount: null,
        kycPendingCount: null,
        kycCompletedCount: null,
        pendingPayments: null,
    });

    const { user, staff, settings } = useSelector(
        (store: DefaultRootState | any) => store || {},
    );

    const isEnabledXUtil = user?.config?.isEnabledXUtil ?? false;
    const riderList: any = staff;
    const currentOrgId = settings?.basicDetails?.id || user?.orgId;

    // FE search: filter the already-loaded riders by ID, name, hub name and
    // vendor name instead of round-tripping the search text to the backend.
    // Keyed off the *committed* `payload.searchText` rather than the live input,
    // so the grid only moves when the user hits Search.
    const FE_SEARCH_KEYS = ['id', 'name', 'hub', 'vendor', 'city', 'phone'];
    const appliedSearchText = payload?.searchText ?? '';
    const filteredRiderList = useMemo(() => {
        const term = appliedSearchText?.trim()?.toLowerCase();
        if (!term) {
            return riderList;
        }
        const lineItems = (riderList?.lineItems ?? []).filter((row: any) =>
            FE_SEARCH_KEYS.some((key) =>
                String(row?.[key] ?? '')
                    .toLowerCase()
                    .includes(term),
            ),
        );
        return {
            ...riderList,
            lineItems,
            totalCount: lineItems.length,
        };
    }, [riderList, appliedSearchText]);

    // Drives the Search CTA's enabled state: there is nothing to submit while
    // the box already matches what the grid is showing.
    const isSearchDirty =
        (searchTextValue ?? '').trim() !== (appliedSearchText ?? '').trim();

    const styles: any = useRiderManagementStyles({
        containerHeight: containerHeight,
    });
    const commonStyles: any = useCommonStyles();
    const permissions = getPermissions('rider_management');

    const dispatchGetRiders = useCallback(
        (data: any, onSuccess: Function, onError: Function) => {
            const fetchStaff = getAllBlueDartStaffs;
            dispatch(fetchStaff(data, onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchGetRiderCounts = useCallback(
        (data: any, onSuccess: Function, onError: Function) => {
            dispatch(getAllBlueDartCounts(data, onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchGetShiftTimingsList = useCallback(
        (data: any, onSuccess: Function, onError: Function) => {
            dispatch(getShiftTimingsList(data, onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchGetVendorList = useCallback(
        (onSuccess: Function, onError: Function) => {
            dispatch(getBluedartFilterVendors(onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchGetBluedartHubs = useCallback(
        (filters: any, onSuccess: Function, onError: Function) => {
            dispatch(getAllBluedartHubs(filters, onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchGetCities = useCallback(
        (onSuccess: Function, onError: Function) => {
            dispatch(getAllBluedartServiceCentreCities(onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchGetZones = useCallback(
        (onSuccess: Function, onError: Function) => {
            dispatch(getAllBluedartServiceCentreZones(onSuccess, onError));
        },
        [dispatch],
    );

    const dispatchResetKycDetails = useCallback(() => {
        dispatch(resetKycDetails());
    }, [dispatch]);

    const fetchRidersData = useCallback(
        (customPayload?: any) => {
            setLoading(true);
            const requestPayload = preProcessPayload(customPayload || payload);
            dispatchGetRiders(
                requestPayload,
                (data: any) => {
                    setLoading(false);
                },
                (error: any) => {
                    setLoading(false);
                },
            );
        },
        [payload, dispatchGetRiders],
    );

    const fetchRiderCounts = useCallback(
        (customPayload?: any) => {
            // Same normalisation the list call uses, so the cards and the grid
            // resolve the four filters identically. Status cards, paging and
            // search are list-only and deliberately not sent
            const { hubIds, vendorIds, cities, zones } = preProcessPayload(
                customPayload || payload,
            );
            dispatchGetRiderCounts(
                { hubIds, vendorIds, cities, zones },
                (resp: any) => {
                    setRiderCounts(resp?.response);
                    //setCountsLoading(false);
                },
                (error: any) => {
                    console.log('Rider counts error', error);
                    //setCountsLoading(false);
                },
            );
        },
        [payload, dispatchGetRiderCounts],
    );

    useEffect(() => {
        if (!permissions.read) {
            props?.history?.push(`/${getActiveInitRoute()}`);
        }
        dispatchGetBluedartHubs(
            {},
            (resp: any) => {
                console.log('dispatchGetBluedartHubs success', resp);
            },
            (err: any) => {
                console.log('dispatchGetBluedartHubs error', err);
            },
        );
        dispatchGetVendorList(
            (resp: any) => {
                console.log('dispatchGetVendorList success', resp);
            },
            (err: any) => {
                console.log('dispatchGetVendorList error', err);
            },
        );
        dispatchGetCities(
            () => {},
            () => {},
        );
        dispatchGetZones(
            () => {},
            () => {},
        );
        fetchRiderCounts();
    }, []);

    useEffect(() => {
        fetchRidersData();
        fetchRiderCounts();
    }, [payload]);

    useEffect(() => {
        if (!!containerRef?.current?.offsetHeight && !containerHeight) {
            const height = containerRef.current.offsetHeight;
            setContainerHeight(height);
        }
    }, [containerRef?.current?.offsetHeight, containerHeight]);

    useEffect(() => {
        const hubIds = filterValues?.hubIds?.map((hub: any) => hub.value);
        dispatchGetShiftTimingsList(
            {
                hubIds: hubIds?.length > 0 ? hubIds : null,
            },
            (resp: any) => {
                console.log('Shift timings resp', resp);
            },
            (err: any) => {
                console.log('Shift timings err', err);
            },
        );
    }, [filterValues?.hubIds]);

    const handleRowClick = (e: any, rider: any) => {
        // Navigate to rider details page if needed
    };

    const getColumnComponentValue = (
        row: any,
        column: any,
        handleAction: any,
    ) => {
        // Every text column renders through CellText, which keeps rows to a
        // single line and exposes the untruncated value on hover. Columns that
        // declare a `chipVariant` render as a colour-coded pill instead.
        const isChip = !!column?.chipVariant;
        const textCell = (value: any) => (
            <CellText
                value={value}
                maxChars={column?.maxChars}
                ellipsis={column?.ellipsis}
                align={column?.align}
                chip={isChip}
                className={
                    isChip
                        ? clsx(styles.valueChip, styles[column.chipVariant])
                        : styles.tableValue
                }
                emptyClassName={isChip ? styles.chipEmpty : styles.tableValue}
            />
        );

        // Columns flagged `copyable` swap the plain pill for one with a copy
        // affordance — the value is meant to be lifted out, not just read.
        const copyCell = (value: any) => (
            <CopyableChip
                value={value}
                label={column?.copyLabel}
                chipClassName={clsx(
                    styles.valueChip,
                    styles[column.chipVariant],
                )}
                emptyClassName={styles.chipEmpty}
            />
        );

        let compValue: any = textCell(row?.[column?.mappingKey]);

        switch (column?.mappingKey) {
            case 'name':
                compValue = textCell(row?.name);
                break;

            case 'phoneNumber':
                compValue = copyCell(row?.phone);
                break;

            case 'vendor':
            case 'city':
            case 'zone':
            case 'hub':
                compValue = textCell(row?.[column?.mappingKey]);
                break;

            case 'daysPending':
                compValue = textCell(row?.daysPending);
                break;

            case 'lastWorkingDate':
                compValue = textCell(
                    row?.lastWorkingDate
                        ? new Date(row.lastWorkingDate).toLocaleDateString(
                              'en-IN',
                              {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                              },
                          )
                        : '',
                );
                break;

            case 'kycStatus': {
                const isKycVerified = !!row?.kycVerified;
                compValue = (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                    >
                        <Box
                            className={clsx([
                                commonStyles.chip,
                                isKycVerified
                                    ? styles.kycCompleted
                                    : styles.kycPending,
                            ])}
                        >
                            <Typography
                                component={'span'}
                                className={commonStyles.chipText}
                            >
                                {isKycVerified ? 'KYC Verified' : 'KYC Pending'}
                            </Typography>
                        </Box>
                    </Box>
                );
                break;
            }

            /*case 'status':
                const isActive =
                    row?.status?.toUpperCase() === 'ACTIVE' ||
                    row?.isActive === true;
                compValue = (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                    >
                        <Box
                            className={clsx([
                                commonStyles.chip,
                                isActive
                                    ? styles.statusActive
                                    : styles.statusInactive,
                            ])}
                        >
                            <Typography
                                component={'span'}
                                className={commonStyles.chipText}
                            >
                                {isActive ? 'Active' : 'Inactive'}
                            </Typography>
                        </Box>
                    </Box>
                );
                break;*/

            case 'empId':
                compValue = copyCell(row?.staffCustCode);
                break;

            // Icon-only actions keep the last column narrow; the action name
            // lives in the tooltip.
            case 'action': {
                // DR-1: deactivation is raised by the Rider Approver alone — not Super Admin,
                // not Regional Approver, and never Rider Viewer. The API rejects the others, so the
                // control is hidden for them rather than failing on click.
                const isRiderApprover =
                    !isBdRbacScopingEnabled() || isBlueDartRiderApprover();
                const canDeactivate =
                    row?.status !== 'DEACTIVATED' &&
                    row?.bgvStatus !== 'FAILED' &&
                    isRiderApprover;

                // DR-5/DR-6: reactivation follows the same route, and is offered only where the
                // reason the rider was deactivated for permits a return. Shown disabled rather
                // than hidden, with the blocking reason named, so it is clear WHY it is not
                // available — hiding it would look like the feature is missing.
                const isDeactivated = row?.status === 'DEACTIVATED';
                const canReactivate =
                    isDeactivated &&
                    isRiderApprover &&
                    isBdDeactivationEnabled();
                const rehireBlocked = row?.rehireEligible !== 'YES';
                const rehireTooltip =
                    row?.rehireEligible === 'NA'
                        ? 'Reactivation is permanently disabled for this rider.'
                        : rehireBlocked
                          ? `Not eligible for rehire (reason: ${
                                row?.deactivationReason ??
                                row?.deactivationReasonCode ??
                                'unknown'
                            }).`
                          : 'Reactivate';
                compValue = (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        style={{ gap: 4 }}
                    >
                        <Tooltip title="View" arrow placement="top">
                            <ActionIcon
                                size="small"
                                aria-label="View rider"
                                className={styles.actionButton}
                                onClick={(event: any) => {
                                    event.stopPropagation();
                                    openViewModal?.(row);
                                }}
                            >
                                <VisibilityIcon fontSize="small" />
                            </ActionIcon>
                        </Tooltip>
                        {canReactivate && (
                            <Tooltip
                                title={rehireTooltip}
                                arrow
                                placement="top"
                            >
                                {/* A disabled MUI button swallows pointer events, so the tooltip
                                    needs a wrapper to hang off — otherwise the reason never
                                    surfaces on precisely the rows that need it. */}
                                <span>
                                    <ActionIcon
                                        size="small"
                                        aria-label="Reactivate rider"
                                        className={styles.actionButton}
                                        disabled={rehireBlocked}
                                        onClick={(event: any) => {
                                            event.stopPropagation();
                                            setReactivateConfirmRider(row);
                                        }}
                                    >
                                        <ReplayIcon fontSize="small" />
                                    </ActionIcon>
                                </span>
                            </Tooltip>
                        )}
                        {permissions?.update && isMpinEnabled() && (
                            <Tooltip title="PIN" arrow placement="top">
                                <ActionIcon
                                    size="small"
                                    aria-label="PIN"
                                    className={styles.actionButton}
                                    onClick={(event: any) => {
                                        event.stopPropagation();
                                        setMpinRider(row);
                                    }}
                                >
                                    <VpnKeyOutlinedIcon fontSize="small" />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        {canDeactivate && (
                            <Tooltip title="Deactivate" arrow placement="top">
                                <ActionIcon
                                    size="small"
                                    aria-label="Deactivate rider"
                                    className={styles.actionButtonDanger}
                                    onClick={(event: any) => {
                                        event.stopPropagation();
                                        setDeactivateConfirmRider(row);
                                    }}
                                >
                                    <BlockIcon fontSize="small" />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Box>
                );
                break;
            }

            default:
                break;
        }
        return compValue;
    };

    const prepareFilterCard = (card: any) => {
        const count =
            riderCounts?.[card?.valueMappingKey as keyof typeof riderCounts];
        switch (card?.type) {
            default:
                return (
                    <Box>
                        <Typography className={styles.cardHeader}>
                            {card?.name}
                        </Typography>
                        <Typography className={styles.cardSubHeader}>
                            {count ?? 0}
                        </Typography>
                    </Box>
                );
        }
    };

    const handleFilterCardClick = (card: any) => {
        handleActiveFilterCard(card);
    };

    const prepareFilterOptions = () => {
        return [
            {
                type: 'MULTISELECT',
                label: 'Hubs',
                name: 'hubIds',
                value: filterValues?.hubIds,
                options:
                    staff?.hubs?.map((item: any) => ({
                        label: item.name,
                        value: item.id,
                    })) ?? [],
                onChange: (e: any) => {
                    setFilterValues({
                        ...filterValues,
                        hubIds: e?.target?.value,
                    });
                },
            },
            {
                type: 'MULTISELECT',
                label: 'Vendors',
                name: 'vendorIds',
                value: filterValues?.vendorIds,
                // `/bluedart/vendors` → { orgId, vendorCode, name }, listed as
                // "VendorName(vendorCode)".
                options:
                    staff?.bdFilterVendors?.map((item: any) => ({
                        label: !!item?.vendorCode
                            ? `${item?.name}(${item?.vendorCode})`
                            : (item?.name ?? ''),
                        value: item?.orgId,
                    })) ?? [],
                onChange: (e: any) => {
                    setFilterValues({
                        ...filterValues,
                        vendorIds: e?.target?.value,
                    });
                },
            },
            {
                type: 'MULTISELECT',
                label: 'City',
                name: 'cities',
                value: filterValues?.cities,
                options:
                    staff?.bdCities?.map((c: string) => ({
                        label: c,
                        value: c,
                    })) ?? [],
                onChange: (e: any) => {
                    setFilterValues({
                        ...filterValues,
                        cities: e?.target?.value,
                    });
                },
            },
            {
                type: 'MULTISELECT',
                label: 'Zone',
                name: 'zones',
                value: filterValues?.zones,
                options:
                    staff?.bdZones?.map((z: string) => ({
                        label: z,
                        value: z,
                    })) ?? [],
                onChange: (e: any) => {
                    setFilterValues({
                        ...filterValues,
                        zones: e?.target?.value,
                    });
                },
            },
            {
                type: 'MULTISELECT',
                label: 'Shifts',
                name: 'shifts',
                value: filterValues?.shifts,
                options:
                    staff?.shifts?.map((item: any) => ({
                        label: item?.shortName,
                        value: item?.id,
                    })) ?? [],
                onChange: (e: any) => {
                    setFilterValues({
                        ...filterValues,
                        shifts: e?.target?.value,
                    });
                },
            },
        ];
    };

    // Filters shown as pickers in the toolbar. The status cards and `shifts`
    // are driven separately, so they stay out of this list.
    const TOOLBAR_FILTER_NAMES = ['hubIds', 'vendorIds', 'cities', 'zones'];

    // Every option list here is already narrowed server-side to the caller's scope, so a filter
    // with a single option is not a choice — it is the scope itself. Those render as a static
    // badge instead (a Regional Approver sees "Zone: EAST", not a one-item dropdown), while filters
    // with real alternatives stay interactive. Driven purely by option count, so it keeps working
    // as someone's scope grows or shrinks and needs no role checks.
    const scopedToolbarFilters = useMemo(
        () =>
            prepareFilterOptions().filter((opt: any) =>
                TOOLBAR_FILTER_NAMES.includes(opt?.name),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            filterValues,
            staff?.hubs,
            staff?.bdFilterVendors,
            staff?.bdCities,
            staff?.bdZones,
        ],
    );

    const toolbarFilterOptions = useMemo(
        () =>
            scopedToolbarFilters.filter(
                (opt: any) => (opt?.options?.length ?? 0) > 1,
            ),
        [scopedToolbarFilters],
    );

    // A scoped role whose backend scope resolved to nothing. Distinct from "no riders match":
    // an unassigned approver sees an empty grid that looks identical to a genuinely empty result,
    // and would otherwise file a bug instead of asking for access.
    const hasNoScopeAssigned = useMemo(
        () =>
            isBdRbacScopingEnabled() &&
            getBdScopeType() !== null &&
            getBdScopeType() !== 'GLOBAL' &&
            (getBdScopeValues()?.length ?? 0) === 0,
        [],
    );

    const scopeBadges = useMemo(
        () =>
            scopedToolbarFilters
                .filter((opt: any) => (opt?.options?.length ?? 0) === 1)
                .map((opt: any) => ({
                    name: opt?.name,
                    label: opt?.label,
                    value: opt?.options?.[0]?.label ?? opt?.options?.[0]?.value,
                })),
        [scopedToolbarFilters],
    );

    // Commit a single filter: the picker hands back the full selection, so the
    // local state and the request payload are updated in one shot.
    const handleFilterChange = (name: string, selected: any[]) => {
        const next = { ...filterValues };
        if (!selected?.length) {
            delete next[name];
        } else {
            next[name] = selected;
        }
        setFilterValues(next);
        // `undefined` rather than `[]` so a cleared filter looks exactly like a
        // filter that was never applied once `preProcessPayload` runs.
        onFilterApplied({
            ...next,
            [name]: selected?.length ? selected : undefined,
        });
    };

    // Chip removal in FilteredState used to only touch the payload, leaving
    // `filterValues` — and therefore the chip itself — in place.
    const handleRemoveFilter = (name: string) => {
        handleFilterChange(name, []);
    };

    // Drops a single selected value, leaving the rest of that filter applied.
    const handleRemoveFilterValue = (name: string, option: any) => {
        const current = filterValues?.[name] ?? [];
        handleFilterChange(
            name,
            current.filter(
                (item: any) => String(item?.value) !== String(option?.value),
            ),
        );
    };

    const handleClearAllFilters = () => {
        // Blank out every key that is currently applied, not just the toolbar
        // ones, so the payload can't keep a filter the UI no longer shows.
        const cleared: any = {};
        Object.keys(filterValues ?? {}).forEach((name) => {
            cleared[name] = undefined;
        });
        TOOLBAR_FILTER_NAMES.forEach((name) => {
            cleared[name] = undefined;
        });
        setFilterValues({});
        onFilterApplied(cleared);
    };

    const activeFilterCount = TOOLBAR_FILTER_NAMES.reduce(
        (count, name) => count + (filterValues?.[name]?.length ? 1 : 0),
        0,
    );

    const handleSearchSubmit = () => {
        onManualSearch(searchTextValue);
    };

    const handleSearchClear = () => {
        clearSearch();
        onManualSearchClear();
    };

    const handleConfirmDeactivate = () => {
        if (!deactivateConfirmRider) return;
        dispatch(
            updateStatus(
                { staffId: deactivateConfirmRider?.id, status: 'DEACTIVATED' },
                () => {
                    setDeactivateConfirmRider(null);
                    fetchRidersData?.();
                    fetchRiderCounts?.();
                },
                () => {
                    setDeactivateConfirmRider(null);
                },
            ),
        );
    };

    /** DR-6: raises a request for approval; the rider is not restored here. The server re-checks
     * rehire eligibility, so a blocked reason surfaces even if the control was somehow reached. */
    const handleConfirmReactivate = () => {
        if (!reactivateConfirmRider) return;
        dispatch(
            raiseBluedartReactivation(
                reactivateConfirmRider?.id,
                {},
                (response: any) => {
                    showMessage(
                        response?.status?.message ??
                            'Reactivation request submitted',
                        'success',
                    );
                    setReactivateConfirmRider(null);
                    fetchRidersData?.();
                    fetchRiderCounts?.();
                },
                (error: any) => {
                    setReactivateConfirmRider(null);
                    showMessage(
                        error?.status?.message ??
                            'Could not raise the reactivation request',
                        'error',
                    );
                },
            ),
        );
    };

    const openRiderModal = (rider?: any) => {
        setSelectedRider(rider);
        setIsModalOpen(true);
    };

    const closeRiderModal = () => {
        setIsModalOpen(false);
        fetchRidersData();
        setSelectedRider(null);
    };

    const openViewModal = (rider?: any) => {
        setSelectedRider(rider);
        setIsViewModalOpen(true);
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedRider(null);
    };

    const openBulkUploadModal = (type?: any) => {
        setBulkUploadType(type);
        setBulkModalOpen(true);
    };
    const closeBulkUploadModal = () => {
        setBulkModalOpen(false);
        fetchRidersData();
        setBulkUploadType(null);
    };

    const handleSelect = (data: any) => {
        if (data && data?.id) {
            const currentIndex = selected.indexOf(data?.id);
            const newChecked = [...selected];

            if (currentIndex === -1) {
                newChecked.push(data.id);
            } else {
                newChecked.splice(currentIndex, 1);
            }
            setSelected(newChecked);
        }
    };
    const handleAllSelect = (all: any[], data: any) => {
        if (all) {
            if (data && data.length > 0) {
                const newChecked = [];
                for (let index = 0; index < data?.length; index++) {
                    if (index === 50) {
                        break;
                    }
                    const element = data?.[index];
                    if (element && element.id) {
                        newChecked.push(element.id);
                    }
                }
                setSelected(newChecked);
            }
        } else {
            setSelected([]);
        }
    };

    return {
        handleRowClick,
        prepareFilterOptions,
        getColumnComponentValue,
        prepareFilterCard,
        riderList: filteredRiderList,
        pagination,
        handleChangeRowsPerPage,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
        activeFilterCard,
        filterValues,
        setFilterValues,
        searchTextValue,
        permissions,
        loading: loading || countsLoading,
        containerRef,
        styles,
        handleChangePage,
        openRiderModal,
        closeRiderModal,
        selectedRider,
        isModalOpen,
        payload,
        fetchRidersData,
        riderCounts,
        fetchRiderCounts,
        handleFilterCardClick,
        RIDER_FILTER_CARDS,
        isEnabledXUtil,
        clearSearch,
        openBulkUploadModal,
        closeBulkUploadModal,
        bulkUploadType,
        openBulkModal,
        selected,
        handleSelect,
        handleAllSelect,
        isViewModalOpen,
        openViewModal,
        closeViewModal,
        onManualSearch,
        onManualSearchClear,
        deactivateConfirmRider,
        setDeactivateConfirmRider,
        mpinRider,
        setMpinRider,
        handleConfirmDeactivate,
        reactivateConfirmRider,
        setReactivateConfirmRider,
        handleConfirmReactivate,
        toolbarFilterOptions,
        scopeBadges,
        hasNoScopeAssigned,
        handleFilterChange,
        handleRemoveFilter,
        handleRemoveFilterValue,
        handleClearAllFilters,
        activeFilterCount,
        handleSearchSubmit,
        handleSearchClear,
        isSearchDirty,
        appliedSearchText,
    };
};

export default useRiderManagement;
